import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import AuthLayout from "../components/auth/AuthLayout";

function GoogleButton() {
    const handleGoogle = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/auth/callback";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };
    return (
        <button
            type="button"
            onClick={handleGoogle}
            data-testid="google-login-button"
            className="w-full flex items-center justify-center gap-3 border border-gold/60 bg-white hover:bg-cream px-5 py-3 font-sans text-sm text-ink transition-all"
        >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 5.1 29.3 3 24 3C12.4 3 3 12.4 3 24s9.4 21 21 21s21-9.4 21-21c0-1.2-.1-2.5-.4-3.5"/>
                <path fill="#FF3D00" d="M6.3 14.1l6.6 4.8C14.7 15 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 5.1 29.3 3 24 3C16.3 3 9.6 7.4 6.3 14.1"/>
                <path fill="#4CAF50" d="M24 45c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.4-4.5 2.4-7.2 2.4c-5.3 0-9.7-3.4-11.3-8l-6.6 5.1C9.5 40.6 16.2 45 24 45"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.6l6.2 5.2C41.6 35.4 45 30.2 45 24c0-1.2-.1-2.5-.4-3.5"/>
            </svg>
            Continue with Google
        </button>
    );
}

export default function Login() {
    const { login } = useAuth();
    const [search] = useSearchParams();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError("");
        try {
            await login(email, password);
            toast.success("Welcome back to the Gurukula.");
            const next = search.get("next") || "/dashboard";
            navigate(next, { replace: true });
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AuthLayout
            eyebrow="· Shishya Login ·"
            title="Welcome back."
            subtitle="Continue your sadhana. Sign in to access your Gurukula dashboard."
            footer={
                <>
                    New to the Gurukula?{" "}
                    <Link to="/register" data-testid="login-go-register" className="text-maroon underline underline-offset-4">
                        Begin your enrollment
                    </Link>
                </>
            }
        >
            <GoogleButton />
            <div className="flex items-center gap-3 my-6">
                <span className="h-px bg-gold-100 flex-1" />
                <span className="eyebrow text-gold">or with email</span>
                <span className="h-px bg-gold-100 flex-1" />
            </div>

            <form onSubmit={submit} data-testid="login-form" className="space-y-5">
                <div>
                    <label className="eyebrow text-ink/70">Email</label>
                    <input
                        data-testid="login-email-input"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="mt-2 w-full bg-cream border border-gold-100 px-4 py-3 font-serif text-ink focus:outline-none focus:border-maroon"
                        placeholder="name@email.com"
                    />
                </div>
                <div>
                    <label className="eyebrow text-ink/70">Password</label>
                    <input
                        data-testid="login-password-input"
                        type="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="mt-2 w-full bg-cream border border-gold-100 px-4 py-3 font-serif text-ink focus:outline-none focus:border-maroon"
                        placeholder="••••••••"
                    />
                </div>
                {error && (
                    <p data-testid="login-error" className="text-sm text-maroon-700 font-serif">
                        {error}
                    </p>
                )}
                <button
                    type="submit"
                    disabled={submitting}
                    data-testid="login-submit-button"
                    className="btn-primary w-full disabled:opacity-60"
                >
                    {submitting ? "Signing in…" : "Sign in"}
                </button>
            </form>
        </AuthLayout>
    );
}
